import React, { useState } from "react";
import { imgBaseUrl } from "../../api/api";
import { formatToDateFull } from "../master/FormatToDateTime";
import { GRADE_SCALE } from "./marksheetUtils";

/* ---------------------------------------------------------
   Settings / school info
--------------------------------------------------------- */

export const useSetting = () => {
  const [setting] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("setting") || "{}") || {};
    } catch (e) {
      return {};
    }
  });
  return setting;
};

export const getLogo = (s) => (s?.logo_img ? imgBaseUrl + s.logo_img : "");

export const schoolAddress = (s) =>
  [s?.village, s?.district, s?.state, s?.pin_code].filter(Boolean).join(", ");

export const schoolContact = (s) => {
  const phones = [s?.mobile, s?.secondary_mobile].filter(Boolean).join(", ");
  return [phones && `Mobile: ${phones}`, s?.email && `Email: ${s.email}`]
    .filter(Boolean)
    .join("   |   ");
};

export const getStudentFields = (data, batch) => {
  const info = data?.student_info || {};
  return [
    { label: "Student Name", value: info.name },
    { label: "Father's Name", value: info.father_name },
    { label: "Mother's Name", value: info.mother_name },
    {
      label: "Date of Birth",
      value: info.dob ? formatToDateFull(info.dob) : "",
    },
    { label: "Class", value: batch?.course?.title },
    { label: "Section", value: batch?.title },
    { label: "SR. No", value: info.registration_no },
    { label: "Roll No", value: data?.roll_number },
    { label: "Apaar ID", value: info.apaar_id },
    { label: "NIC ID", value: info.nic_id },
  ];
};

/* ---------------------------------------------------------
   Styles
   The <style> lives INSIDE each template so it is copied
   along with the markup by both "Print" and "Print V2".

   The old shared rules force `border: none`, `color: black`
   and `text-transform: uppercase` (all !important) on every
   child of .a4-page-marksheet. Every rule below is scoped as
   `.a4-page-marksheet.msN ...` (higher specificity) and uses
   !important where it has to beat those.
   In css strings, `&` = `.a4-page-marksheet.<scope>`.
--------------------------------------------------------- */

const BASE_CSS = `
& { border: 0 !important; line-height: 1.35; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
& * { text-transform: none !important; font-size: 11px; }
& table { width: 100%; border-collapse: collapse; margin: 0; }
& th, & td { padding: 3px 6px; vertical-align: middle; }
& p { margin: 0; }
& small { font-size: 9px; opacity: .7; }
& .ms-left { text-align: left !important; }
& .ms-legend { display: flex; flex-wrap: wrap; gap: 2px 12px; }
& .ms-legend * { font-size: 9.5px; }
`;

export const MarksheetStyle = ({ setting, scope, css }) => {
  const logo = getLogo(setting);
  const scoped = (BASE_CSS + css).replace(/&/g, `.a4-page-marksheet.${scope}`);
  return (
    <style>{`.a4-page-marksheet::before{background-image:${
      logo ? `url(${logo})` : "none"
    };}${scoped}`}</style>
  );
};

/* ---------------------------------------------------------
   Shared building blocks (styled per template through CSS)
--------------------------------------------------------- */

export const MarksTable = ({
  rows,
  series,
  seriesTotals,
  overall,
  bars = false,
}) => (
  <table className="ms-marks">
    <thead>
      <tr>
        <th className="ms-left">Subject</th>
        {series.map((exam, i) => (
          <th key={i}>{exam?.series_name}</th>
        ))}
        <th>Total</th>
        <th>Grade</th>
        {bars && <th>Score</th>}
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={r.id ?? i}>
          <td className="ms-left">{r.name}</td>
          {r.marks.map((m, j) => (
            <td key={j}>{m.obtained}</td>
          ))}
          <td>
            {r.obtained}
            {r.max ? <small> / {r.max}</small> : null}
          </td>
          <td>{r.grade}</td>
          {bars && (
            <td className="ms-bar">
              <span>
                <i style={{ width: `${Math.min(100, r.percentage)}%` }} />
              </span>
            </td>
          )}
        </tr>
      ))}
    </tbody>
    <tfoot>
      <tr>
        <td className="ms-left">Grand Total</td>
        {seriesTotals.map((t, i) => (
          <td key={i}>{t.obtained}</td>
        ))}
        <td>
          {overall.obtained}
          <small> / {overall.total}</small>
        </td>
        <td>{overall.grade}</td>
        {bars && <td />}
      </tr>
    </tfoot>
  </table>
);

export const CO_AREAS = [
  "Work Education",
  "Art Education & Drawing",
  "Health & Physical Education",
  "Music & Dance",
  "GK",
];

// Grades are left blank on purpose so teachers can write them in.
export const CoScholastic = () => (
  <table className="ms-cos">
    <thead>
      <tr>
        <th>Co-Scholastic Area</th>
        <th>Term 1</th>
        <th>Term 2</th>
      </tr>
    </thead>
    <tbody>
      {CO_AREAS.map((area) => (
        <tr key={area}>
          <td>{area}</td>
          <td></td>
          <td></td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const GradeLegend = () => (
  <div className="ms-legend">
    <b>Grading scale</b>
    {GRADE_SCALE.map((g) => (
      <span key={g.grade}>
        <b>{g.grade}</b> {g.min}-{g.max}
      </span>
    ))}
  </div>
);
